import React, { createRef } from "react";
import { observer } from "mobx-react";
import { Modal, Row, Col, Form, Button, Table, Badge, Spinner } from "react-bootstrap";
import { CreateEditCustomerOrderStore, Part } from "../../../../stores";
import { wrapInput } from "../../../../common/FormChangeWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { SearchHistoryModal } from "./SearchHistoryModal";
import { CrossesModal } from "./Components/CrossesModal";

interface IProps {
    createEditStore: CreateEditCustomerOrderStore;
    isOpened: boolean;
    source: string;
    onClose: () => void;
    addPositionToOrder: (part: Part, count: number) => void;
    searchByPair: (brand: string, article: string, sortBy: string) => void;
    switchToRussianCarsSearch: () => void;
}

interface IState {
    article: string;
    searchBy: string;
    sortBy: string;
    searchHistoryModalOpened: boolean;
    loadingBranches: { [key: string]: boolean };
    branchParts: { [key: string]: Part[] };
    expandedRows: { [key: string]: boolean };
    isLoadingAnalogues: boolean;
    minStock: number;
    maxDays: number;
    minPrice: number;
    filterByDays: boolean;
    filterByPrice: boolean;
    articleFilter: string;
}

@observer
export class AddPositionModal extends React.Component<IProps, IState> {
    constructor(props: any) {
        super(props);
        this.state = {
            article: "",
            searchBy: "article",
            searchHistoryModalOpened: false,
            loadingBranches: {},
            branchParts: {},
            expandedRows: {},
            sortBy: "price",
            isLoadingAnalogues: false,
            minStock: 1,
            maxDays: 3,
            minPrice: 1000,
            filterByDays: false,
            filterByPrice: false,
            articleFilter: "",
        }
        this.modalRef = createRef<any>();
    }
    private commonStore = this.props.createEditStore._rootStore.commonStore;

    private articleTextEditRef = createRef<any>();
    private countLoadingAnalogues = 300;
    private originalArticleId;
    private originalBrandGroupId;
    private originalArticle;
    private originalBrand;
    private modalRef;

    handleGetPartsByBranch = async (rowKey: string, articleId: number) => {
        this.setState(prevState => ({
            loadingBranches: { ...prevState.loadingBranches, [rowKey]: true },
            expandedRows: { ...prevState.expandedRows, [rowKey]: true }
        }));

        try {
            const result = await this.props.createEditStore.getPartsByBranch(
                this.props.createEditStore.searchKey,
                articleId
            );

            this.setState(prevState => ({
                branchParts: { ...prevState.branchParts, [rowKey]: result },
                loadingBranches: { ...prevState.loadingBranches, [rowKey]: false }
            }));
        } catch (error) {
            console.error("Error loading branch parts:", error);
            this.setState(prevState => ({
                loadingBranches: { ...prevState.loadingBranches, [rowKey]: false }
            }));
        }
    }

    handleToggleBranchParts = (rowKey: string, articleId: number) => {
        const isExpanded = this.state.expandedRows[rowKey];

        if (isExpanded) {
            this.setState(prevState => ({
                expandedRows: { ...prevState.expandedRows, [rowKey]: false }
            }));
        } else {
            if (!this.state.branchParts[rowKey]) {
                this.handleGetPartsByBranch(rowKey, articleId);
            } else {
                this.setState(prevState => ({
                    expandedRows: { ...prevState.expandedRows, [rowKey]: true }
                }));
            }
        }
    }

    handleSearchByPair = (brand: string, article: string) => {
        this.setState({
            searchBy: 'pair',
            branchParts: {},
            expandedRows: {}
        });
        this.props.searchByPair(brand, article, this.state.sortBy);
    }

    handleSearchByArticle = () => {
        this.setState({
            searchBy: 'article',
            branchParts: {},
            expandedRows: {},
            articleFilter: ""
        });
        this.props.createEditStore.searchByArticle(this.state.article);
    }
    handleGetVerifiedArticles = (brandGroupId: number, articleId: number) => {
        this.props.createEditStore.getVerifiedArticles(brandGroupId, articleId);
    }

    handleVerifyArticle = async (analogueBrandGroupId: number, analogueArticleId: number, analogueBrand: string, analogueArticle: string) => {
        const buttonVariant = this.getButtonVariant(analogueBrandGroupId, analogueArticleId);
        let confirmMessage = '';
        let successMessage = 'Аналог успешно привязан';

        if (buttonVariant === 'primary') {
            confirmMessage = `Вы хотите привязать аналог ${analogueBrand} ${analogueArticle} к оригинальному ${this.originalArticle}?`;
        } else if (buttonVariant === 'dark') {
            confirmMessage = `Вы хотите подтвердить привязку аналога ${analogueBrand} ${analogueArticle} к оригинальному ${this.originalArticle}?`;
        } else if (buttonVariant === 'success') {
            confirmMessage = `Вы хотите отвязать аналог ${analogueBrand} ${analogueArticle} от оригинального ${this.originalArticle}?`;
            successMessage = 'Аналог успешно отвязан';
        }

        if (window.confirm(confirmMessage)) {
            if (buttonVariant === 'success') {
                this.props.createEditStore.unverifyArticle(this.originalBrandGroupId, this.originalArticleId, analogueBrandGroupId, analogueArticleId, () => {
                    console.log(successMessage);
                });
            } else {
                this.props.createEditStore.verifyArticle(this.originalBrandGroupId, this.originalArticleId, analogueBrandGroupId, analogueArticleId, () => {
                    console.log(successMessage);
                });
            }
        }
    }

    handleOpenBrandUrl = (brandUrl: string) => {
        const targetUrl = brandUrl.replace(/\{ARTICLE\}/g, encodeURIComponent(this.originalArticle));
        window.open(targetUrl, "_blank", "noopener, noreferrer");
    }

    getButtonVariant = (brandGroupId: number, articleId: number): "primary" | "success" | "dark" => {
        const analogs = this.props.createEditStore.verifiedArticles?.analogs || [];

        const match = analogs.find((a) => a.articleId === articleId && a.brandGroupId === brandGroupId);
        if (!match) return "primary";
        if (match.status === 1) return "success";
        if (match.status === 0) return "dark";
        return "primary";
    }

    onChangeArticle = (fieldName: string, value: string) => {
        this.setState({
            article: value
        });
    }

    changeSorting = (sortBy: string) => {
        if (this.state.sortBy !== sortBy) {
            this.props.createEditStore.getResultsByGroup(this.props.createEditStore.searchKey, 1, sortBy, 0, 7)
            this.props.createEditStore.getResultsByGroup(this.props.createEditStore.searchKey, 2, sortBy, 0, 2)
            this.props.createEditStore.getResultsByGroup(this.props.createEditStore.searchKey, 3, sortBy, 0, 100)
            this.setState({
                sortBy: sortBy
            });
        }
    }

    onChangeAddCount = (part: Part, group: number, rowNum: number, newCount: number) => {
        if (newCount > 0) {
            if (newCount !== part.toOrderCount)
                this.props.createEditStore.changeAddToOrderCount(group, rowNum, Math.max(newCount, part.minQuantity));
        }
    }

    renderBranchParts(rowKey:string, articleId: number) {
        const { loadingBranches, branchParts, expandedRows } = this.state;

        if (!expandedRows[rowKey]) return null;

        if (loadingBranches[rowKey]) {
            return (
                <tr>
                    <td colSpan={10} className="text-center">
                        <Spinner animation="border" size="sm" />
                    </td>
                </tr>
            );
        }

        if (!branchParts[rowKey] || branchParts[rowKey].length === 0) {
            return (
                <tr>
                    <td colSpan={10} className="text-center">
                        Нет данных по филиалам
                    </td>
                </tr>
            );
        }

        return branchParts[rowKey].map((part, index) => (
            <tr key={`branch_${rowKey}`} style={{ backgroundColor: `${part.color || 'none'}` }}>
                <td className="text-center">
                    {part.brandUrl == null ?
                        <> {part.brand} </>
                        : <button className="custom-link-btn" onClick={(e) => {
                            e.preventDefault();
                            this.handleOpenBrandUrl(part.brandUrl);
                        }}>
                            {part.brand}
                        </button>
                    }
                </td>
                <td className="text-center">{part.article}</td>
                <td style={{ wordBreak: 'break-word' }}>{part.description}</td>
                <td className="text-center">{part.count}</td>
                <td className="text-center">{part.deliveryTimeString}</td>
                <td className={"text-center"} style={{ width: 95 }}>{part.timeToRegionString}</td>
                <td className="text-center">
                    {part.isAllowDiscountRefund ?
                        <Badge variant="success" title={`Обратный выкуп ${part.refundDiscount === 0 ? 'без уценки' : 'c уценкой ' + part.refundDiscount + '%'}`}>
                            {part.refundDiscount === 0 ? <FontAwesomeIcon icon="check" /> : part.refundDiscount}
                        </Badge>
                        : <Badge variant="warning" title="Обратный выкуп невозможен">
                            <FontAwesomeIcon icon="times" />
                        </Badge>
                    }
                </td>
                <td className="text-center">{part.supName}</td>
                <td className="text-center">{part.cost}</td>
                <td className={"text-center" + ((part.count < part.toOrderCount) ? " table-danger" : "")} style={{ width: "225px", margin: "0 auto" }}>
                    <div className="form-inline">
                        <Form.Control type="number"
                            value={part.toOrderCount.toString()}
                            size="sm"
                            style={{ width: "60px", marginRight: "10px" }}
                            onChange={wrapInput((fieldName: string, value: string) => {
                                const newCount = parseInt(value);
                                if (newCount > 0) {
                                    this.setState(prevState => ({
                                        branchParts: {
                                            ...prevState.branchParts,
                                            [rowKey]: prevState.branchParts[rowKey].map(p =>
                                                p.articleId === part.articleId && p.supCode === part.supCode  ? { ...p, toOrderCount: newCount } : p
                                            )
                                        }
                                    }));
                                }
                            })}
                        />
                        <Button type="button" variant={this.getButtonVariant(part.brandGroupId, part.articleId)} size="sm" disabled={part.count < part.toOrderCount} onClick={() => this.props.addPositionToOrder(part, part.toOrderCount)}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                this.handleVerifyArticle(part.brandGroupId, part.articleId, part.brand, part.article);
                            }}>
                            <FontAwesomeIcon icon="plus" />{' '}Добавить в заказ
                        </Button>
                    </div>
                </td>
            </tr>
        ));
    }

    renderPartRow(part: Part, group: number, index: number) {
        const { minStock, filterByDays, maxDays, filterByPrice, minPrice, articleFilter } = this.state;

        const stock = part.count;
        const price = part.cost;

        const parseDays = (daysText: string): number => {
            if (daysText === "На складе") return 0;
            const match = daysText.match(/(\d+)\((\d+)\)/);
            if (match) return parseInt(match[2]);
            const singleMatch = daysText.match(/(\d+)/);
            return singleMatch ? parseInt(singleMatch[1]) : Infinity;
        };
        const days = parseDays(part.deliveryTimeString);

        const cleanArticle = (article: string): string =>
            article ? article.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() : '';

        const cleanPartArticle = cleanArticle(part.article);
        const cleanFilter = cleanArticle(articleFilter);
        const articleMatch = !cleanFilter || cleanPartArticle.includes(cleanFilter);

        let showRow = articleMatch;
        if (part.deliveryTimeString !== "На складе" && stock < minStock) {
            showRow = false;
        }
        if (filterByDays && days > maxDays) {
            showRow = false;
        }
        if (filterByPrice && price < minPrice) {
            showRow = false;
        }


        if (!showRow) {
            return null;
        }
     
        const rowKey = `${group}_${index}_${part.article}_${part.supCode}`;
        const isExpanded = this.state.expandedRows[rowKey];
        return (
            <React.Fragment key={rowKey}>
                <tr style={{ backgroundColor: `${part.color || 'none'}` }}>
                    <td className="text-center">
                        {part.brandUrl == null ?
                            <> {part.brand} </>
                            : <button className="custom-link-btn" onClick={(e) => {
                                e.preventDefault();
                                this.handleOpenBrandUrl(part.brandUrl);
                            }}>
                                {part.brand}
                            </button>
                        }
                    </td>
                    <td className="text-center">{part.fromTecdoc ? [<span key={1}>{part.article}</span>,
                    <div key={2} title="Кросс есть в Tecdoc" className="tecdoc-badge-icon"></div>]
                        : part.article}</td>
                    <td style={{ wordBreak: 'break-word' }}>{part.description}</td>
                    <td className="text-center">{part.count}</td>
                    <td className="text-center">{part.deliveryTimeString}</td>
                    <td className="text-center" style={{ width: 95 }}>{part.timeToRegionString}</td>
                    <td className="text-center">{part.isAllowDiscountRefund ? <Badge variant="success" title={`Обратный выкуп ${part.refundDiscount === 0 ? 'без уценки' : 'c уценкой ' + part.refundDiscount + '%'}`}>
                        {part.refundDiscount === 0 ? <FontAwesomeIcon icon="check" /> : part.refundDiscount}
                    </Badge>
                        : <Badge variant="warning" title="Обратный выкуп невозможен">
                            <FontAwesomeIcon icon="times" />
                        </Badge>
                    }</td>
                    <td className="text-center">
                        {part.supName}
                        <br />
                       
                        {part.hasBranchParts ?
                            <a href="javascript:void(0)" onClick={() => this.handleToggleBranchParts(rowKey, part.articleId)}>
                                {isExpanded ? 'Скрыть предложения' : 'Наличие в филиалах'}
                            </a> : []}
                    </td>
                    <td className="text-center">{part.cost}</td>
                    <td className={"text-center" + ((part.count < part.toOrderCount) ? " table-danger" : "")} style={{ width: "242px", margin: "0 auto" }}>
                        <div className="form-inline">
                            <Form.Control type="number"
                                value={part.toOrderCount.toString()}
                                size="sm"
                                style={{ width: "55px", marginRight: "5px" }}
                                onChange={wrapInput((fieldName: string, value: string) => {
                                    this.onChangeAddCount(part, group, index, parseInt(value))
                                })}
                            />
                            <Button type="button" variant={this.getButtonVariant(part.brandGroupId, part.articleId)} size="sm" disabled={part.count < part.toOrderCount} onClick={() => this.props.addPositionToOrder(part, part.toOrderCount)}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    this.handleVerifyArticle(part.brandGroupId, part.articleId, part.brand, part.article);
                                }}>
                                <FontAwesomeIcon icon="plus" />{' '}Добавить в заказ
                            </Button>
                            { part.isAnalog && 
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    style={{ marginLeft: "5px" }}
                                    onClick={() => {
                                        this.props.createEditStore.getCrosses(
                                            this.originalArticleId,
                                            part.articleId
                                        );
                                    }}
                                >
                                    ?
                                </Button>
                            }
                        </div>
                    </td>
                </tr>
                {this.renderBranchParts(rowKey, part.articleId)}
            </React.Fragment>
        );
    }

    render() {
        return [<Modal ref={this.modalRef} show={this.props.isOpened} onHide={this.props.onClose} className="modal-xxl" key={"1"}
            onShow={() => {
                this.commonStore.initScrollTop(() => {
                    this.modalRef?.current?._modal?.dialog?.scrollTo(0, 0);
                })
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Добавление позиции в заказ
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Row>
                    <Col sm={12}>
                        <Form className="" inline onSubmit={(e: any) => {
                            e.preventDefault();
                        }}>
                            <Form.Group className="" controlId="formSearch">
                                <Form.Control size="sm"
                                    ref={this.articleTextEditRef}
                                    type="text" value={this.state.article}
                                    onChange={wrapInput(this.onChangeArticle)}
                                    placeholder="Введите артикул..."
                                    style={{ width: "250px" }}
                                    autoComplete="off" />
                            </Form.Group>
                            <Button className="" size="sm" type="submit" onClick={this.handleSearchByArticle}><FontAwesomeIcon icon='search' />{' '}Поиск</Button>
                            <Button className="" size="sm" type="button" onClick={() => {
                                this.setState({ searchHistoryModalOpened: true })
                            }

                            } variant="link" style={{ marginLeft: "30px" }}>
                                <FontAwesomeIcon icon="history" />{' '}История поиска
                            </Button>
                            {/*<Form.Check className="" inline style={{ marginLeft: "30px" }}
                                label="По артикулу" type="radio"
                                defaultChecked={true}
                            />
                            <Form.Check className="" inline
                                label="По русским авто" type="radio"
                                defaultChecked={false}
                                onClick={this.props.switchToRussianCarsSearch}
                            />*/}
                            {this.props.createEditStore.analogues.skip + this.props.createEditStore.analogues.take < this.props.createEditStore.analogues.totalCount ? (
                                <Button className="" size="sm" type="button" style={{ marginLeft: "10px" }} onClick={() => {
                                    this.setState({ isLoadingAnalogues: true });
                                    this.props.createEditStore.getResultsByGroup(
                                        this.props.createEditStore.searchKey,
                                        3,
                                        this.state.sortBy,
                                        this.props.createEditStore.analogues.skip + this.props.createEditStore.analogues.take,
                                        this.props.createEditStore.analogues.totalCount > 1200 ? 1200: this.props.createEditStore.analogues.totalCount);
                                    setTimeout(() => {
                                        this.setState({ isLoadingAnalogues: false });
                                    }, 1500);
                                }} disabled={this.state.isLoadingAnalogues} >
                                    {this.state.isLoadingAnalogues ?
                                        <> <FontAwesomeIcon icon="spinner" spin style={{ marginRight: "6px" }} /> Загрузка... </>
                                        : "Показать всё"
                                    }
                                </Button>
                            ) : []}

                            <div className="d-flex flex-wrap align-items-center mt-2 mb-2">

                                                            <div style={{ position: "relative", display: "inline-block" }}>
                                    <Form.Control className=""
                                        type="text" size="sm"
                                        value={this.state.articleFilter}
                                        onChange={(e) => this.setState({ articleFilter: e.target.value })}
                                        placeholder="Артикул"
                                        style={{ width: "110px", paddingRight: "25px", marginLeft: "15px" }}
                                    />
                                    {this.state.articleFilter && (
                                        <Button className=""
                                            variant="link"
                                            style={{ position: "absolute", right: "1px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", color: "grey" }}
                                            onClick={() => this.setState({ articleFilter: "" })}
                                        > x </Button>
                                    )}
                                </div>

                                <Button className=""
                                    size="sm" variant="danger"
                                    style={{ marginLeft: "20px" }}
                                    onClick={() =>
                                        this.setState({
                                            minStock: 1,
                                            filterByDays: false,
                                            maxDays: 3,
                                            filterByPrice: false,
                                            minPrice: 1000,
                                            articleFilter: "",
                                        })
                                    }
                                    > Сброс </Button>

                                <Form.Label className="" style={{ marginRight: "10px", marginLeft: "15px" }}> Остаток: </Form.Label>
                                <Form.Control className=""
                                    type="number" size="sm"
                                    value={this.state.minStock}
                                    onChange={(e) => this.setState({ minStock: parseInt(e.target.value) || 1 })}
                                    placeholder="шт" min="1" step="1" 
                                    style={{ width: "50px", marginRight: "20px" }}
                                />

                                <Form.Check className="" inline
                                    type="checkbox" id="filter-by-days" label="Срок до:"
                                    checked={this.state.filterByDays}
                                    onChange={(e) => this.setState({ filterByDays: e.target.checked })}
                                />
                                <Form.Control className=""
                                    type="number" size="sm"
                                    value={this.state.maxDays}
                                    onChange={(e) => this.setState({ maxDays: parseInt(e.target.value) || 3 })}
                                    placeholder="дни" min="1"
                                    disabled={!this.state.filterByDays}
                                    style={{ width: "50px", marginRight: "20px" }}
                                />

                                <Form.Check className="" inline
                                    type="checkbox" id="filter-by-price" label="Цена от:"
                                    checked={this.state.filterByPrice}
                                    onChange={(e) => this.setState({ filterByPrice: e.target.checked })}
                                />
                                <Form.Control className=""
                                    type="number" size="sm"
                                    value={this.state.minPrice}
                                    onChange={(e) => this.setState({ minPrice: parseInt(e.target.value) || 1000 })}
                                    placeholder="руб" min="0" step="1000"
                                    disabled={!this.state.filterByPrice}
                                    style={{ width: "70px", marginRight: "20px" }}
                                />
                            </div>
                                
                        </Form>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} style={{ minHeight: "200px" }}>
                        {this.state.searchBy === 'pair' ? <Table bordered striped className="table-xxs">
                            <thead>
                                <tr>
                                    <th className="text-center">Бренд</th>
                                    <th className="text-center">Артикул</th>
                                    <th>Наименование</th>
                                    <th className="text-center">Остаток</th>
                                    <th
                                        style={{ width: "75px", cursor: "pointer" }}
                                        className="text-center"
                                        onClick={() => this.changeSorting("time")}
                                    >
                                        Срок
                                        {this.state.sortBy === "time" ?
                                            <FontAwesomeIcon icon="sort-down" style={{ float: "right" }} />
                                            : []
                                        }
                                    </th>
                                    <th>Клиенту</th>
                                    <th></th>
                                    <th className="text-center">Склад</th>
                                    <th
                                        style={{ cursor: "pointer" }}
                                        className="text-center"
                                        onClick={() => this.changeSorting("price")}
                                    >
                                        Цена
                                        {this.state.sortBy === "price" ?
                                            <FontAwesomeIcon icon="sort-down" style={{ float: "right" }} />
                                            : []
                                        }
                                    </th>
                                    <th className="text-center" style={{ width: "240px" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr key={"1_header"}>
                                    <td colSpan={10}><h5>Запрашиваемый артикул</h5></td>
                                </tr>
                                {this.props.createEditStore.whatWant.items.map((w, index) => this.renderPartRow(w, 1, index))}
                                {this.props.createEditStore.whatWant.skip + this.props.createEditStore.whatWant.take < this.props.createEditStore.whatWant.totalCount ?
                                    <tr>
                                        <td colSpan={10} className="text-center">
                                            <a href="javascript:void(0)"
                                                onClick={() => this.props.createEditStore.getResultsByGroup(
                                                    this.props.createEditStore.searchKey,
                                                    1,
                                                    this.state.sortBy,
                                                    this.props.createEditStore.whatWant.skip + this.props.createEditStore.whatWant.take,
                                                    this.props.createEditStore.whatWant.take)}>Показать ещё предложения</a>
                                        </td>
                                    </tr>

                                    : []}
                                {
                                    this.props.createEditStore.originals != undefined && this.props.createEditStore.originals.items.length > 0 ? [

                                        <tr key={"2_header"}>
                                            <td colSpan={10}><h5>Оригинальные замены</h5></td>
                                        </tr>,
                                        this.props.createEditStore.originals.items.map((w, index) => this.renderPartRow(w, 2, index))]
                                        : []
                                }
                                {this.props.createEditStore.originals.skip + this.props.createEditStore.originals.take < this.props.createEditStore.originals.totalCount ?
                                    <tr>
                                        <td colSpan={10} className="text-center">
                                            <a onClick={() => this.props.createEditStore.getResultsByGroup(
                                                this.props.createEditStore.searchKey,
                                                2,
                                                this.state.sortBy,
                                                this.props.createEditStore.originals.skip + this.props.createEditStore.originals.take,
                                                this.props.createEditStore.originals.take)}>Показать ещё предложения</a>
                                        </td>
                                    </tr>

                                    : []}
                                <tr key={"3_header"}>
                                    <td colSpan={10} ><h5>Неоригинальные замены</h5></td>
                                </tr>

                                {this.props.createEditStore.analogues.items.map((w, index) => this.renderPartRow(w, 3, index))}
                                {this.props.createEditStore.analogues.skip + this.props.createEditStore.analogues.take < this.props.createEditStore.analogues.totalCount ?
                                    <tr>
                                        <td colSpan={10} className="text-center">
                                            <a href="javascript:void(0)"
                                                onClick={() => this.props.createEditStore.getResultsByGroup(
                                                    this.props.createEditStore.searchKey,
                                                    3,
                                                    this.state.sortBy,
                                                    this.props.createEditStore.analogues.skip + this.props.createEditStore.analogues.take,
                                                    this.countLoadingAnalogues)}>Показать ещё предложения</a>
                                        </td>
                                    </tr>

                                    : []}
                            </tbody>
                        </Table>
                            : <Table bordered striped className="table-xxs">
                                <thead>
                                    <tr>
                                        <th style={{ width: "150px" }}>Бренд</th>
                                        <th style={{ width: "150px" }}>Артикул</th>
                                        <th>Наименование</th>
                                        <th style={{ width: "150px" }}></th>
                                    </tr>
                                </thead>
                                <tbody>

                                    {this.props.createEditStore.articles.map((w, index) => <tr key={index}>
                                        <td className="text-center">{w.brand}</td>
                                        <td className="text-center">{w.article}</td>
                                        <td className="text-left">{w.description}</td>
                                        <td className="text-center"><Button size="sm" onClick={() => {
                                            this.originalArticle = w.article;
                                            this.originalBrand = w.brand;
                                            this.originalBrandGroupId = w.brandGroupId;
                                            this.originalArticleId = w.articleId;
                                            this.handleSearchByPair(w.brand, w.article);
                                            this.handleGetVerifiedArticles(w.brandGroupId, w.articleId);
                                        }}
                                        >Цены и аналоги</Button></td>
                                    </tr>)}
                                </tbody>
                            </Table>
                        }
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>,
        <SearchHistoryModal
            key={"2"}
            source={this.props.source}
            isOpened={this.state.searchHistoryModalOpened}
            onClose={() => this.setState({ searchHistoryModalOpened: false })}
            searchCallback={(brand: string, article: string, brandGroupId: number, articleId: number) => {
                this.setState({
                    searchHistoryModalOpened: false,
                    article: article
                });
                this.originalArticle = article;
                this.originalArticleId = articleId;
                this.originalBrandGroupId = brandGroupId;
                this.handleSearchByPair(brand, article);
                this.handleGetVerifiedArticles(this.originalBrandGroupId, this.originalArticleId);
            }}
            />,
            <CrossesModal
                key={"3"}
                crosses={this.props.createEditStore.crosses}
                onClose={() =>
                    this.props.createEditStore.closeCrosses()
                }
                deleteCross={(firstId, secondId) =>
                    this.props.createEditStore.deleteCross(firstId, secondId)
                }
                isCrossDeleted={
                    this.props.createEditStore.isCrossDeleted.bind(
                        this.props.createEditStore
                    )
                }
            />]
    }
}